'use client';

import { useState, type FormEvent } from 'react';

import { formatPhone, isValidMobile, phoneDigits } from '@/shared/lib/phone';
import { Button } from '@/shared/ui/button';
import { TextField } from '@/shared/ui/text-field';

import styles from './recipient-step.module.scss';

export type Recipient = { name: string; phone: string; email: string };

type Errors = Partial<Record<keyof Recipient, string | undefined>>;

function validate(value: Recipient): Errors {
  const errors: Errors = {};
  if (value.name.trim().length < 2) errors.name = 'Укажите имя';
  if (!isValidMobile(value.phone)) errors.phone = 'Проверьте номер телефона';
  if (!/^\S+@\S+\.\S+$/.test(value.email.trim())) errors.email = 'Проверьте e-mail';
  return errors;
}

export function recipientSummary(value: Recipient) {
  return `${value.name.trim()} · ${formatPhone(value.phone)} · ${value.email.trim()}`;
}

/** Step 01 "Получатель": name, phone and e-mail for the receipt (the design shows it collapsed). */
export function RecipientStep({
  initial,
  onDone,
}: {
  initial: Recipient;
  onDone: (value: Recipient) => void;
}) {
  const [value, setValue] = useState<Recipient>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const set = (key: keyof Recipient, next: string) => {
    setValue((current) => ({ ...current, [key]: next }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const found = validate(value);
    setErrors(found);
    const first = (['name', 'phone', 'email'] as const).find((key) => found[key]);
    if (first) {
      document.getElementById(`co-${first}`)?.focus();
      return;
    }
    onDone({ name: value.name.trim(), phone: value.phone, email: value.email.trim() });
  };
  return (
    <form className={styles.root} onSubmit={submit} noValidate>
      <div className={styles.grid}>
        <TextField
          id="co-name"
          label="Имя и фамилия"
          autoComplete="name"
          value={value.name}
          error={errors.name}
          onChange={(event) => set('name', event.target.value)}
        />
        <TextField
          id="co-phone"
          label="Телефон"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+7 999 999-99-99"
          value={formatPhone(value.phone)}
          error={errors.phone}
          onChange={(event) => set('phone', phoneDigits(event.target.value))}
        />
        <TextField
          id="co-email"
          label="E-mail для чека"
          type="email"
          autoComplete="email"
          value={value.email}
          error={errors.email}
          onChange={(event) => set('email', event.target.value)}
          className={styles.wide}
        />
      </div>
      <p className={styles.caption}>Статус заказа придёт в SMS, регистрация не нужна.</p>
      <Button type="submit" size="lg" className={styles.submit}>
        Продолжить
      </Button>
    </form>
  );
}
